import type { Locale, Translator } from '@developerz/i18n';
import { createTranslator, flatten, isLocale } from '@developerz/i18n';
import en from '../locales/en.json' with { type: 'json' };
import es from '../locales/es.json' with { type: 'json' };
import zh from '../locales/zh.json' with { type: 'json' };

const enFlat = flatten(en);

// Non-en locale catalogs, each merged over the en baseline (en first, locale
// overrides second) so missing keys fall through to en. Same shape as
// `apps/marketing/src/i18n.ts`, including the STATIC import: a runtime
// `import(`../locales/${locale}.json`)` silently fails to resolve in a bundle.
//
// `es` is a PARTIAL catalog and the merge is what makes that honest — every key
// it omits resolves to the en string, never `⟦key⟧`. `onboarding.*` is the only
// namespace translated in FULL, because it is the one screen a new account
// cannot skip: a Spanish speaker reads the marketing site in Spanish, signs up,
// and the first authenticated page asks for a plan and an API key. Beyond it the
// catalog is ad hoc — scattered keys across five other namespaces, translated as
// someone happened to touch them — so the REST of the console is English, a
// mixed UI that is deliberate rather than finished.
//
// The coverage is RECORDED per namespace in `ES_COVERAGE` (`i18n.es.test.ts`),
// by equality in both directions. This comment used to name the scope itself
// (`onboarding.*` plus one door) and to say that file "measures the gap" —
// neither was true: the file computed no gap, and those five other namespaces
// were already in `es.json`. Read the record for the numbers, not this text.
//
// `zh` is partial the same way, translated screen by screen in the order a new
// Chinese-speaking account meets them: sign-in, the navigation and onboarding
// first. Its coverage is RECORDED in `ZH_COVERAGE` (`i18n.zh.test.ts`), which
// also holds it to the marketing site's zh house style (halfwidth punctuation, no
// em dash), so the two surfaces read as one product.
const CATALOGS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  es: { ...enFlat, ...flatten(es) },
  zh: { ...enFlat, ...flatten(zh) },
};

/**
 * Build a locale-aware translator for the given locale string.
 *
 * Lookup order:
 *   1. Supported non-en locale with a catalog → its merged catalog (locale
 *      wins, missing keys fall through to en).
 *   2. Unknown / unsupported / en locale → the `en` translator.
 */
export function translatorForLocale(locale: string): Translator {
  if (!isLocale(locale) || locale === 'en') {
    // `en` is the locale of what this translator RESOLVES, not of what was
    // asked for: an unknown locale falls back to the English catalog, and a
    // formatter reading `t.locale` must group numbers the way this copy reads.
    return createTranslator(enFlat, 'en');
  }
  const catalog = CATALOGS[locale];
  return catalog ? createTranslator(catalog, locale) : createTranslator(enFlat, 'en');
}

/**
 * Resolve the visitor's browser locale to a supported primary subtag.
 *
 * The dashboard is a client-only SPA — there's no per-request locale to
 * plumb through the way `apps/marketing`'s URL-prefix routing has — so this
 * reads `navigator.language` (e.g. `'es-MX'`), takes the primary subtag
 * before any `-`, lowercases it, and falls back to `'en'` for anything
 * `isLocale` doesn't recognise, or if `navigator` throws/is unavailable
 * (a non-browser test environment).
 */
export function resolveBrowserLocale(): string {
  try {
    const lang = navigator?.language;
    if (!lang) return 'en';
    const primary = lang.split('-')[0]?.toLowerCase() ?? '';
    return isLocale(primary) ? primary : 'en';
  } catch {
    return 'en';
  }
}

/**
 * The language chosen in the top bar's picker (`components/LanguagePicker.tsx`,
 * #6222), per browser, under this key. A raw locale code, like `dz-theme` holds
 * a raw theme name — not `lib/local-pref.ts`'s JSON, which would store `"en"`
 * where a reader opening devtools expects `en`. Absent means "follow the
 * browser".
 */
export const LOCALE_STORAGE_KEY = 'dz-locale';

/**
 * Each supported locale named in its own language, the way the picker lists
 * them: a reader finds their language on a page they cannot read. Data, not
 * copy, so never a `t()` key — the name is the same in every catalog. Typed
 * against `Locale`, so a locale added to `SUPPORTED_LOCALES` fails the build
 * here until it has a name. `中文`, not marketing's `简体中文`: the spelling the
 * issue asked for, and the shorter one fits the top bar.
 */
export const LOCALE_ENDONYMS: Readonly<Record<Locale, string>> = {
  en: 'English',
  es: 'Español',
  zh: '中文',
};

/** `globalThis.localStorage`, read lazily: a private-mode browser can throw on
 *  the PROPERTY ACCESS, before any method call (`lib/local-pref.ts` has the
 *  same note), so the read sits inside each caller's try block. */
const browserStorage = (): Storage | undefined => globalThis.localStorage;

/**
 * The stored language choice, or `null` when there is none to honour: nothing
 * stored, a value that is not a supported locale (a hand edit, a locale since
 * retired), or storage that throws or is absent. Each of those means "follow
 * the browser", so a caller never has to tell them apart.
 */
export function readStoredLocale(
  storage: () => Storage | undefined = browserStorage,
): Locale | null {
  try {
    const stored = storage()?.getItem(LOCALE_STORAGE_KEY);
    return stored !== null && stored !== undefined && isLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Save a language choice, or clear it (`null`, the picker's "Browser default").
 * A storage failure is swallowed: the reload that follows simply lands on the
 * browser's language, the same page the reader had before choosing.
 */
export function writeStoredLocale(
  locale: Locale | null,
  storage: () => Storage | undefined = browserStorage,
): void {
  try {
    if (locale === null) storage()?.removeItem(LOCALE_STORAGE_KEY);
    else storage()?.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // A write that failed (a full quota) can leave the PREVIOUS choice stored, and the
    // reload would then land on it rather than the browser's language. Clear it; if
    // storage is gone altogether (private mode) that throws too, and nothing sticks.
    try {
      storage()?.removeItem(LOCALE_STORAGE_KEY);
    } catch {
      // ponytail: private-mode storage throws; the choice just does not stick.
    }
  }
}

/**
 * The locale the dashboard renders in: a stored supported choice first, then
 * the browser's (already narrowed to a supported locale or `en` by
 * {@link resolveBrowserLocale}). Pure, so the order is testable without a
 * browser; `stored` is re-checked here rather than trusted, because a caller
 * may hand in a raw storage read.
 */
export function resolveLocale(stored: string | null, browser: string): string {
  return stored !== null && isLocale(stored) ? stored : browser;
}

/**
 * Single translator instance for the dashboard app, resolved from the stored
 * language choice, else the visitor's browser locale.
 *
 * This used to add "groundwork only: `CATALOGS` above ships only `en` today, so
 * a non-en locale still falls through to the `en` translator" — untrue since
 * `es` landed in `CATALOGS`. A Spanish visitor gets the merged `es` catalog:
 * `onboarding.*` in Spanish plus the recorded scatter, every other key falling
 * through to `en`, which is what the merge is for.
 *
 * Built ONCE, at load: the picker changes the language by saving the choice and
 * reloading the page, never by rebinding this (`lib/time.ts`'s formatters are
 * built off `t.locale` at load too, and `testing/formatter-seam.test.ts` holds
 * `t` to one binding for that reason).
 */
export const t = translatorForLocale(resolveLocale(readStoredLocale(), resolveBrowserLocale()));

/**
 * The ONE singular/plural choice: `tPlural(key, count, vars)` renders
 * `<key>_one` or `<key>_other` by the locale's CLDR category, with `{n}` =
 * `count` (`packages/i18n/src/plural.ts`). Never `count === 1 ? t(a) : t(b)` —
 * that drifted into three key conventions and shipped "1 new lines". A module
 * that takes an injected translator calls `tr.plural` the same way.
 */
export const tPlural = t.plural;

/**
 * Label the document with the language its copy RESOLVES to (WCAG 3.1.1, plan
 * 101 slice 17 row 17-26). `index.html` ships `lang="en"` for the first paint;
 * a Spanish browser then reads the `es` catalog, so `lang` follows `t.locale`,
 * never `navigator.language` (an unknown language renders the en catalog).
 */
export function syncDocumentLang(
  translator: Translator,
  root: HTMLElement | undefined = typeof document === 'undefined'
    ? undefined
    : document.documentElement,
): void {
  if (root !== undefined) root.lang = translator.locale;
}

syncDocumentLang(t);
